import { BILLING_ERROR_CODES } from '@aysar/contracts';

import { type ApiError } from '@/shared/utils/transform-error/transform-error.types';

/** What the entitlement surface should say about a refusal, and what to offer next. */
export interface EntitlementErrorCopy {
  /** A `payments:entitlements.error.*` key. */
  i18nKey: string;
  /**
   * A colleague acted between this operator loading the member and writing — and
   * they were right. The answer is a **re-read**, never a retry: revoking is
   * irreversible, so the second attempt is refused again by design.
   */
  isConflict: boolean;
  /**
   * The entitlement is not there any more (404).
   *
   * Distinct from a conflict, because there is nothing to go and read: the dialog
   * closes rather than leaving an operator looking at a row that is gone.
   */
  isGone: boolean;
  /**
   * The control this refusal belongs on, when the api could not name one itself.
   *
   * `ProductNotFoundError` carries no `details`, so `mapServerErrors` has nothing
   * to place and would put "that plan no longer exists" above the form — next to
   * nothing the operator can change. A refusal the api cannot place is placed by
   * the client, on the field that earned it (P21b).
   */
  field: 'productKey' | null;
}

/**
 * The refusals **this** surface can meet, and the key that answers each.
 *
 * ## A sibling of `paymentErrorCopy`, deliberately not an extension of it
 *
 * That util's own docblock scopes it to the order desk and says the entitlement
 * codes are "deliberately absent — nothing on the order desk can produce them,
 * and mapping a code would be copy nothing could ever show". The same argument
 * runs the other way: the five order-transition codes cannot reach a grant or a
 * revoke, and one util answering for both surfaces would be a map whose entries
 * are unreachable from whichever caller is reading it.
 *
 * ## Two entitlement codes are NOT here, and that is the finding
 *
 * `billing_entitlement_already_active` reads exactly like the guard against the
 * silent overwrite this screen is built around — and it is **thrown nowhere**.
 * Its class docblock and its code docblock both call it "the refusal branch of
 * the grant claim"; there is no claim, and `grantOrExtend` extends the live row
 * instead. Mapping it would put a sentence on screen that can never appear and
 * imply a protection that does not exist. `billing_entitlement_period_required`
 * is thrown by the **paid** channel's `grant` and is unreachable from the promo
 * route, where an open-ended comp is the point.
 *
 * `null` for anything unmapped, so the caller shows the api's own sentence rather
 * than a translation this module guessed at — which also keeps a transport
 * failure (`status: 0`) from being dressed up as a colleague's decision.
 */
const KEY_BY_CODE: Readonly<Record<string, string>> = {
  [BILLING_ERROR_CODES.entitlementNotClaimable]: 'entitlements.error.notClaimable',
  [BILLING_ERROR_CODES.entitlementNotFound]: 'entitlements.error.notFound',
  [BILLING_ERROR_CODES.productNotFound]: 'entitlements.error.productGone',
};

export function entitlementErrorCopy(
  error: ApiError | null | undefined,
): EntitlementErrorCopy | null {
  if (error === null || error === undefined) {
    return null;
  }

  const mapped = KEY_BY_CODE[error.code];

  if (mapped === undefined) {
    return null;
  }

  const isProductGone = error.code === BILLING_ERROR_CODES.productNotFound;

  return {
    i18nKey: mapped,
    isConflict: error.status === 409,
    // A withdrawn plan is a 404 too, and it is NOT "gone" in this sense: the
    // entitlement the operator is looking at is fine, and the recovery is to
    // pick a different plan rather than to close the dialog.
    isGone: error.status === 404 && !isProductGone,
    field: isProductGone ? 'productKey' : null,
  };
}
